import clsx from 'clsx';
import keycode from 'keycode';
import React, { cloneElement } from 'react';
import PropTypes from 'prop-types';
import all from 'prop-types-extra/lib/all';
import warning from 'warning';
import { getClassSet, prefix, splitBsProps } from './utils/bootstrapUtils';
import createChainedFunction from './utils/createChainedFunction';
import ValidComponentChildren from './utils/ValidComponentChildren';
import { useTabContainer, useNavbar } from './context/TabContext';

// TODO: Should we expose `<NavItem>` as `<Nav.Item>`?

// TODO: This `bsStyle` is very unlike the others. Should we rename it?

// TODO: `pullRight` and `pullLeft` don't render right outside of `navbar`.
// Consider renaming or replacing them.
import { jsx as _jsx } from "react/jsx-runtime";
class Nav extends React.Component {
  constructor(props) {
    super(props);
    this.navRef = /*#__PURE__*/React.createRef(); // Create a ref for the Nav component
  }
  componentDidUpdate() {
    if (!this._needsRefocus) {
      return;
    }
    this._needsRefocus = false;
    const {
      children
    } = this.props;
    const {
      activeKey,
      activeHref
    } = this.getActiveProps();
    const activeChild = ValidComponentChildren.find(children, child => this.isActive(child, activeKey, activeHref));
    const childrenArray = ValidComponentChildren.toArray(children);
    const activeChildIndex = childrenArray.indexOf(activeChild);
    const childNodes = this.navRef.current?.children; // Use the ref to access DOM nodes
    const activeNode = childNodes && childNodes[activeChildIndex];
    if (!activeNode || !activeNode.firstChild) {
      return;
    }
    activeNode.firstChild.focus();
  }
  handleTabKeyDown(onSelect, event) {
    let nextActiveChild;
    switch (event.keyCode) {
      case keycode.codes.left:
      case keycode.codes.up:
        nextActiveChild = this.getNextActiveChild(-1);
        break;
      case keycode.codes.right:
      case keycode.codes.down:
        nextActiveChild = this.getNextActiveChild(1);
        break;
      default:
        // It was a different key; don't handle this keypress.
        return;
    }
    event.preventDefault();
    if (onSelect && nextActiveChild && nextActiveChild.props.eventKey != null) {
      onSelect(nextActiveChild.props.eventKey);
    }
    this._needsRefocus = true;
  }
  getActiveProps() {
    const {
      tabContainer
    } = this.props;
    if (tabContainer) {
      process.env.NODE_ENV !== "production" ? warning(this.props.activeKey == null && !this.props.activeHref, 'Specifying a `<Nav>` `activeKey` or `activeHref` in the context of ' + 'a `<TabContainer>` is not supported. Instead use `<TabContainer ' + `activeKey={${this.props.activeKey}} />\`.`) : void 0;
      return tabContainer;
    }
    return this.props;
  }
  getNextActiveChild(offset) {
    const {
      children
    } = this.props;
    const validChildren = ValidComponentChildren.filter(children, child => child.props.eventKey != null && !child.props.disabled);
    const {
      activeKey,
      activeHref
    } = this.getActiveProps();
    const activeChild = ValidComponentChildren.find(children, child => this.isActive(child, activeKey, activeHref));

    // This assumes the active child is not disabled.
    const activeChildIndex = validChildren.indexOf(activeChild);
    if (activeChildIndex === -1) {
      // Something has gone wrong. Select the first valid child we can find.
      return validChildren[0];
    }
    let nextIndex = activeChildIndex + offset;
    const numValidChildren = validChildren.length;
    if (nextIndex >= numValidChildren) {
      nextIndex = 0;
    } else if (nextIndex < 0) {
      nextIndex = numValidChildren - 1;
    }
    return validChildren[nextIndex];
  }
  getTabProps(child, tabContainer, navRole, active, onSelect) {
    if (!tabContainer && navRole !== 'tablist') {
      // No tab props here.
      return null;
    }
    let {
      id,
      'aria-controls': controls,
      role,
      onKeyDown,
      tabIndex
    } = child.props;
    const {
      eventKey
    } = child.props;
    if (tabContainer) {
      process.env.NODE_ENV !== "production" ? warning(!id && !controls, 'In the context of a `<TabContainer>`, `<NavItem>`s are given ' + 'generated `id` and `aria-controls` attributes for the sake of ' + 'proper component accessibility. Any provided ones will be ignored. ' + 'To control these attributes directly, provide a `generateChildId` ' + 'prop to the parent `<TabContainer>`.') : void 0;
      id = tabContainer.getTabId(eventKey);
      controls = tabContainer.getPaneId(eventKey);
    }
    if (navRole === 'tablist') {
      role = role || 'tab';
      onKeyDown = createChainedFunction(event => this.handleTabKeyDown(onSelect, event), onKeyDown);
      tabIndex = active ? tabIndex : -1;
    }
    return {
      id,
      role,
      onKeyDown,
      'aria-controls': controls,
      tabIndex
    };
  }
  isActive(_ref, activeKey, activeHref) {
    let {
      props
    } = _ref;
    if (props.active || activeKey != null && props.eventKey === activeKey || activeHref && props.href === activeHref) {
      return true;
    }
    return props.active;
  }
  render() {
    const {
      stacked,
      justified,
      onSelect,
      role: propsRole,
      navbar: propsNavbar,
      pullRight,
      pullLeft,
      className,
      children,
      tabContainer,
      navbarContext,
      ...props
    } = this.props;
    const role = propsRole || (tabContainer ? 'tablist' : null);
    const {
      activeKey,
      activeHref
    } = this.getActiveProps();
    delete props.activeKey; // Accessed via this.getActiveProps().
    delete props.activeHref; // Accessed via this.getActiveProps().

    const [bsProps, elementProps] = splitBsProps(props);
    const classes = {
      ...getClassSet(bsProps),
      [prefix(bsProps, 'stacked')]: stacked,
      [prefix(bsProps, 'justified')]: justified
    };
    const navbar = propsNavbar != null ? propsNavbar : navbarContext;
    let pullLeftClassName;
    let pullRightClassName;
    if (navbar) {
      const navbarProps = navbarContext || {
        bsClass: 'navbar'
      };
      classes[prefix(navbarProps, 'nav')] = true;
      pullRightClassName = prefix(navbarProps, 'right');
      pullLeftClassName = prefix(navbarProps, 'left');
    } else {
      pullRightClassName = 'pull-right';
      pullLeftClassName = 'pull-left';
    }
    classes[pullRightClassName] = pullRight;
    classes[pullLeftClassName] = pullLeft;
    return /*#__PURE__*/_jsx("ul", {
      ...elementProps,
      role: role,
      className: clsx(className, classes),
      ref: this.navRef // Attach the ref to the <ul> element
      ,
      children: ValidComponentChildren.map(children, child => {
        const active = this.isActive(child, activeKey, activeHref);
        const childOnSelect = createChainedFunction(child.props.onSelect, onSelect, navbar && navbar.onSelect, tabContainer && tabContainer.onSelect);
        return /*#__PURE__*/cloneElement(child, {
          ...this.getTabProps(child, tabContainer, role, active, childOnSelect),
          active,
          activeKey,
          activeHref,
          onSelect: childOnSelect
        });
      })
    });
  }
}
function NavWrapper(_ref2) {
  let {
    justified = false,
    pullRight = false,
    pullLeft = false,
    stacked = false,
    bsClass: componentBsClass = 'nav',
    bsStyle,
    ...props
  } = _ref2;
  const tabContainer = useTabContainer();
  const navbarContext = useNavbar();
  return /*#__PURE__*/_jsx(Nav, {
    justified: justified,
    pullRight: pullRight,
    pullLeft: pullLeft,
    stacked: stacked,
    bsClass: componentBsClass,
    bsStyle: bsStyle,
    ...props,
    tabContainer: tabContainer,
    navbarContext: navbarContext
  });
}
export default NavWrapper;